import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "h-full w-[260px] flex-shrink-0 border-r border-[#E5E5E5] bg-[#DADDE2]"
}
const _hoisted_2 = { class: "flex flex-col gap-4 p-4 pt-6" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = { class: "font-semibold" }
const _hoisted_5 = { class: "h-[calc(100%-140px)] overflow-y-auto px-4" }
const _hoisted_6 = {
  key: 0,
  class: "flex items-center justify-center py-8"
}
const _hoisted_7 = {
  key: 1,
  class: "space-y-1"
}
const _hoisted_8 = { class: "mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280]" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "min-w-0 flex-1" }
const _hoisted_11 = { class: "mt-0.5 truncate text-xs text-[#626468]" }
const _hoisted_12 = { class: "mt-1 flex items-center gap-1 text-xs text-[#6B7280]" }
const _hoisted_13 = ["title", "onClick"]
const _hoisted_14 = {
  key: 0,
  class: "px-2 py-4 text-center"
}
const _hoisted_15 = { class: "text-sm text-[#6B7280]" }

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChatAltIcon, ClockIcon, TrashIcon } from '@heroicons/vue/outline';

import { useAuraStore } from '@/stores/modules/aura';
import { can } from '@/composables/useAuth';
import { Permission } from '@/types';
import { formatRelativeDate } from '@/utils/datetime';
import type { AuraConversation } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraSidebar',
  props: {
    isHistoryLoading: { type: Boolean }
  },
  emits: ["select", "create", "delete"],
  setup(__props: any, { emit: __emit }) {

/**
 * AuraSidebar - Sidebar with conversation history
 * Single Responsibility: Display and manage conversation list
 */
const { t } = useI18n();
const store = useAuraStore();

// Props


// Emits
const emit = __emit;

// Permissions
const canViewHistory = computed(() => can(Permission.VIEW_AURA_CONVERSATION));
const canCreateConversation = computed(() => can(Permission.CREATE_AURA_CONVERSATION));
const canDeleteConversation = computed(() => can(Permission.DELETE_AURA_CONVERSATION));

// Computed from store
const hasConversations = computed(() => store.conversations.length > 0);
const conversations = computed(() => store.conversations);
const currentConversationId = computed(() => store.currentConversation?.id || null);
// Handlers
const selectConversation = (conversation: AuraConversation) => {
    emit('select', conversation);
};

const createConversation = () => {
    emit('create');
};

const deleteConversation = (conversation: AuraConversation) => {
    emit('delete', conversation);
};

return (_ctx: any,_cache: any) => {
  return (canViewHistory.value)
    ? (_openBlock(), _createElementBlock("aside", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[0] || (_cache[0] = _createElementVNode("h1", { class: "px-2 text-xl font-bold text-gray-800" }, "mobcall AURA™", -1)),
          _createElementVNode("button", {
            disabled: !canCreateConversation.value,
            class: _normalizeClass([
                    'flex items-center justify-center rounded-xl px-4 py-3 text-white transition-colors',
                    canCreateConversation.value
                        ? 'bg-[#242B59] hover:bg-[#1a203f]'
                        : 'cursor-not-allowed bg-gray-400',
                ]),
            onClick: createConversation
          }, [
            _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('aura.chat.newChat')), 1)
          ], 10, _hoisted_3)
        ]),
        _createElementVNode("div", _hoisted_5, [
          (_ctx.isHistoryLoading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[1] || (_cache[1] = [
                _createElementVNode("div", { class: "h-6 w-6 animate-spin rounded-full border-2 border-[#242B59] border-t-transparent" }, null, -1)
              ])))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)('aura.chat.history')), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(conversations.value, (conversation) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: conversation.id,
                    class: _normalizeClass([
                        'group flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors',
                        currentConversationId.value === conversation.id
                            ? 'bg-white shadow-sm'
                            : 'hover:bg-white/50',
                    ]),
                    onClick: ($event: any) => (selectConversation(conversation))
                  }, [
                    _createVNode(_unref(ChatAltIcon), {
                      class: _normalizeClass([
                            'mt-0.5 h-4 w-4 flex-shrink-0',
                            currentConversationId.value === conversation.id
                                ? 'text-[#48DDC3]'
                                : 'text-[#242B59]',
                        ])
                    }, null, 8, ["class"]),
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("p", {
                        class: _normalizeClass([
                                'truncate text-sm font-medium',
                                currentConversationId.value === conversation.id
                                    ? 'text-[#242B59]'
                                    : 'text-[#242B59]',
                            ])
                      }, _toDisplayString(conversation.title || 'Untitled'), 3),
                      _createElementVNode("p", _hoisted_11, _toDisplayString(conversation.title || 'No messages'), 1),
                      _createElementVNode("p", _hoisted_12, [
                        _createVNode(_unref(ClockIcon), { class: "h-3 w-3" }),
                        _createTextVNode(" " + _toDisplayString(_unref(formatRelativeDate)(conversation.createdAt) || conversation.createdAt), 1)
                      ])
                    ]),
                    (canDeleteConversation.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: _normalizeClass([
                            'ml-1 flex-shrink-0 rounded p-1 opacity-0 transition-opacity',
                            'hover:bg-red-100 hover:text-red-600',
                            'group-hover:opacity-100',
                            'focus:opacity-100',
                        ]),
                          title: _unref(t)('aura.chat.deleteConversation'),
                          onClick: _withModifiers(($event: any) => (deleteConversation(conversation)), ["stop"])
                        }, [
                          _createVNode(_unref(TrashIcon), { class: "h-4 w-4" })
                        ], 8, _hoisted_13))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_9))
                }), 128)),
                (!hasConversations.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("p", _hoisted_15, _toDisplayString(_unref(t)('aura.conversation.noConversations')), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]))
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})